/**
 * Modrinth API Service for fetching mod information
 * API Documentation: https://docs.modrinth.com/api-spec/
 */

export interface ModrinthProject {
  id: string;
  slug: string;
  project_type: string;
  team: string;
  title: string;
  description: string;
  body: string;
  published: string;
  updated: string;
  approved: string;
  status: string;
  moderator_message?: string;
  license: {
    id: string;
    name: string;
    url?: string;
  };
  client_side: 'required' | 'optional' | 'unsupported';
  server_side: 'required' | 'optional' | 'unsupported';
  downloads: number;
  followers: number;
  categories: string[];
  additional_categories: string[];
  game_versions: string[];
  loaders: string[];
  versions: string[];
  icon_url?: string;
  issues_url?: string;
  source_url?: string;
  wiki_url?: string;
  discord_url?: string;
  donation_urls: Array<{
    id: string;
    platform: string;
    url: string;
  }>;
  gallery: Array<{
    url: string;
    featured: boolean;
    title?: string;
    description?: string;
    created: string;
  }>;
}

export interface ModrinthVersion {
  name: string;
  version_number: string;
  changelog?: string;
  dependencies: Array<{
    version_id?: string;
    project_id?: string;
    file_name?: string;
    dependency_type: 'required' | 'optional' | 'incompatible' | 'embedded';
  }>;
  game_versions: string[];
  version_type: 'release' | 'beta' | 'alpha';
  loaders: string[];
  featured: boolean;
  status: string;
  requested_status?: string;
  id: string;
  project_id: string;
  author_id: string;
  date_published: string;
  downloads: number;
  files: Array<{
    hashes: {
      sha512: string;
      sha1: string;
    };
    url: string;
    filename: string;
    primary: boolean;
    size: number;
    file_type?: string;
  }>;
}

export interface ModrinthSearchResult {
  hits: Array<{
    project_id: string;
    project_type: string;
    slug: string;
    author: string;
    title: string;
    description: string;
    categories: string[];
    display_categories: string[];
    versions: string[];
    downloads: number;
    follows: number;
    icon_url?: string;
    date_created: string;
    date_modified: string;
    latest_version?: string;
    license: string;
    client_side: string;
    server_side: string;
    gallery: string[];
    color?: number;
  }>;
  offset: number;
  limit: number;
  total_hits: number;
}

class ModrinthService {
  private readonly BASE_URL = 'https://api.modrinth.com/v2';
  private readonly CDN_URL = 'https://cdn.modrinth.com/data';

  /**
   * Extract project slug from various Modrinth URL formats
   */
  extractProjectSlug(url: string): string | null {
    try {
      const urlObj = new URL(url);
      
      // Handle different Modrinth URL formats:
      // https://modrinth.com/mod/tardis-refined
      // https://modrinth.com/mod/tardis-refined/version/1.20.1
      // https://cdn.modrinth.com/data/project_id/versions/...
      
      if (urlObj.hostname === 'modrinth.com') {
        const pathParts = urlObj.pathname.split('/');
        if (pathParts.length >= 3 && pathParts[1] === 'mod') {
          return pathParts[2];
        }
      }
      
      if (urlObj.hostname === 'cdn.modrinth.com') {
        const pathParts = urlObj.pathname.split('/');
        if (pathParts.length >= 3 && pathParts[1] === 'data') {
          // This is a project ID, not a slug - we'll need to resolve it
          return pathParts[2];
        }
      }
      
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Get project information by slug or ID
   */
  async getProject(slugOrId: string): Promise<ModrinthProject | null> {
    try {
      const response = await fetch(`${this.BASE_URL}/project/${slugOrId}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch project: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching Modrinth project:', error);
      return null;
    }
  }

  /**
   * Get versions for a project
   */
  async getProjectVersions(
    slugOrId: string,
    options?: {
      loaders?: string[];
      game_versions?: string[];
      featured?: boolean;
    }
  ): Promise<ModrinthVersion[]> {
    try {
      const params = new URLSearchParams();
      if (options?.loaders) {
        params.append('loaders', JSON.stringify(options.loaders));
      }
      if (options?.game_versions) {
        params.append('game_versions', JSON.stringify(options.game_versions));
      }
      if (options?.featured !== undefined) {
        params.append('featured', options.featured.toString());
      }

      const url = `${this.BASE_URL}/project/${slugOrId}/version${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch versions: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching project versions:', error);
      return [];
    }
  }

  /**
   * Search for mods
   */
  async searchMods(
    query: string,
    options?: {
      categories?: string[];
      versions?: string[];
      loaders?: string[];
      project_type?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<ModrinthSearchResult | null> {
    try {
      const params = new URLSearchParams();
      params.append('query', query);
      params.append('facets', JSON.stringify([
        ['project_type:mod'],
        ...(options?.categories ? [options.categories.map(c => `categories:${c}`)] : []),
        ...(options?.versions ? [options.versions.map(v => `versions:${v}`)] : []),
        ...(options?.loaders ? [options.loaders.map(l => `categories:${l}`)] : [])
      ]));
      
      if (options?.limit) {
        params.append('limit', options.limit.toString());
      }
      if (options?.offset) {
        params.append('offset', options.offset.toString());
      }

      const response = await fetch(`${this.BASE_URL}/search?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching mods:', error);
      return null;
    }
  }

  /**
   * Get latest compatible version for specific Minecraft version and loader
   */
  async getLatestCompatibleVersion(
    slugOrId: string,
    minecraftVersion: string,
    loader: string
  ): Promise<ModrinthVersion | null> {
    const versions = await this.getProjectVersions(slugOrId, {
      loaders: [loader],
      game_versions: [minecraftVersion]
    });

    if (versions.length === 0) {
      return null;
    }

    // Sort by date and return the latest
    versions.sort((a, b) => new Date(b.date_published).getTime() - new Date(a.date_published).getTime());
    return versions[0];
  }

  /**
   * Get primary download URL for a version
   */
  getPrimaryDownloadUrl(version: ModrinthVersion): string | null {
    const primaryFile = version.files.find(f => f.primary);
    return primaryFile?.url || version.files[0]?.url || null;
  }

  /**
   * Convert Modrinth project to our Mod interface
   */
  async convertToMod(
    project: ModrinthProject,
    minecraftVersion: string,
    loader: string
  ): Promise<{
    name: string;
    mod_id: string;
    version: string;
    url: string;
    enabled: boolean;
    server_side: boolean;
    client_side: boolean;
    source: 'modrinth';
    project_id: string;
    description: string;
    categories: string[];
    dependencies: Array<{
      mod_id: string;
      required: boolean;
      version_range?: string;
    }>;
    minecraft_versions: string[];
    added_date: string;
    fabric_api_required?: boolean;
  } | null> {
    try {
      const latestVersion = await this.getLatestCompatibleVersion(
        project.slug,
        minecraftVersion,
        loader
      );

      if (!latestVersion) {
        return null;
      }

      const downloadUrl = this.getPrimaryDownloadUrl(latestVersion);
      if (!downloadUrl) {
        return null;
      }

      // Check if this mod requires Fabric API
      const requiresFabricApi = (loader === 'fabric' || loader === 'quilt') && 
        latestVersion.dependencies.some(dep => 
          dep.project_id === 'P7dR8mSH' || // Fabric API project ID
          dep.file_name?.toLowerCase().includes('fabric-api')
        );

      return {
        name: project.title,
        mod_id: project.slug,
        version: latestVersion.version_number,
        url: downloadUrl,
        enabled: true,
        server_side: project.server_side === 'required' || project.server_side === 'optional',
        client_side: project.client_side === 'required' || project.client_side === 'optional',
        source: 'modrinth' as const,
        project_id: project.id,
        description: project.description,
        categories: project.categories,
        dependencies: latestVersion.dependencies
          .filter(dep => dep.dependency_type === 'required')
          .map(dep => ({
            mod_id: dep.project_id || '',
            required: true,
            version_range: dep.version_id
          }))
          .filter(dep => dep.mod_id), // Remove empty mod_ids
        minecraft_versions: latestVersion.game_versions,
        added_date: new Date().toISOString(),
        fabric_api_required: requiresFabricApi
      };
    } catch (error) {
      console.error('Error converting Modrinth project to mod:', error);
      return null;
    }
  }

  /**
   * Get Fabric API mod for specific Minecraft version
   */
  async getFabricApi(minecraftVersion: string): Promise<{
    name: string;
    mod_id: string;
    version: string;
    url: string;
    enabled: boolean;
    server_side: boolean;
    client_side: boolean;
    source: 'modrinth';
    project_id: string;
    description: string;
    categories: string[];
    dependencies: Array<{
      mod_id: string;
      required: boolean;
      version_range?: string;
    }>;
    minecraft_versions: string[];
    added_date: string;
  } | null> {
    try {
      const fabricApiProject = await this.getProject('P7dR8mSH'); // Fabric API project ID
      if (!fabricApiProject) {
        return null;
      }

      return await this.convertToMod(fabricApiProject, minecraftVersion, 'fabric');
    } catch (error) {
      console.error('Error fetching Fabric API:', error);
      return null;
    }
  }

  /**
   * Get popular mods for a specific loader and Minecraft version
   */
  async getPopularMods(
    loader: string,
    minecraftVersion: string,
    category?: string,
    limit: number = 20
  ): Promise<ModrinthSearchResult | null> {
    return this.searchMods('', {
      loaders: [loader],
      versions: [minecraftVersion],
      categories: category ? [category] : undefined,
      limit
    });
  }
}

const modrinthService = new ModrinthService();
export default modrinthService;